import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Popover
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ColorLens as ColorLensIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNotification } from '../../contexts/NotificationContext';
import programaService from '../../services/programaService';
import especialidadService from '../../services/especialidadService';
import { useAuth } from '../../hooks/useAuth';

// Colores predefinidos para el selector
const colorPresets = [
  '#5D1390', '#3F51B5', '#2196F3', '#00BCD4', '#009688',
  '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107',
  '#FF9800', '#FF5722', '#795548', '#9E9E9E', '#607D8B',
  '#E91E63', '#F44336', '#000000', '#424242', '#757575'
];

const ProgramasList = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [programas, setProgramas] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPrograma, setSelectedPrograma] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    especialidad_id: '',
    modalidad: ''
  });
  const [filtersVisible, setFiltersVisible] = useState(false);
  
  // Estado para el selector de color
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const colorPickerOpen = Boolean(colorAnchorEl);

  const isAdmin = user?.rol === 'Administrador';

  // Cargar programas y especialidades
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [programasResponse, especialidadesResponse] = await Promise.all([
          programaService.getProgramas(),
          especialidadService.getEspecialidades()
        ]);
        
        setProgramas(programasResponse.data);
        setEspecialidades(especialidadesResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        showNotification('Error al cargar los datos', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey, showNotification]);

  // Esquema de validación con Yup
  const validationSchema = Yup.object({
    nombre: Yup.string().required('El nombre es requerido'),
    especialidad_id: Yup.number().required('La especialidad es requerida'),
    color: Yup.string()
      .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Ingrese un color hexadecimal válido')
      .required('El color es requerido'),
    duracion_cita: Yup.number()
      .min(15, 'La duración mínima es de 15 minutos')
      .max(180, 'La duración máxima es de 180 minutos')
      .required('La duración es requerida'),
    tarifa: Yup.number()
      .min(0, 'La tarifa no puede ser negativa')
      .required('La tarifa es requerida'),
    modalidad: Yup.string().required('La modalidad es requerida'),
    edad_minima: Yup.number().nullable(),
    edad_maxima: Yup.number().nullable(),
  });

  // Formik para manejar el formulario
  const formik = useFormik({
    initialValues: {
      nombre: '',
      descripcion: '',
      especialidad_id: '',
      color: '#5D1390',
      duracion_cita: 60,
      tarifa: 0,
      modalidad: 'Regular',
      edad_minima: '',
      edad_maxima: '',
      activo: true
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Convertir campos numéricos
        const programaData = {
          ...values,
          edad_minima: values.edad_minima === '' ? null : Number(values.edad_minima),
          edad_maxima: values.edad_maxima === '' ? null : Number(values.edad_maxima),
          tarifa: Number(values.tarifa),
          duracion_cita: Number(values.duracion_cita),
        };

        if (editMode) {
          await programaService.updatePrograma(selectedPrograma.id, programaData);
          showNotification('Programa actualizado con éxito', 'success');
        } else {
          await programaService.createPrograma(programaData);
          showNotification('Programa creado con éxito', 'success');
        }
        setDialogOpen(false);
        setRefreshKey(old => old + 1); // Refrescar lista
      } catch (error) {
        console.error('Error saving programa:', error);
        showNotification(
          `Error al ${editMode ? 'actualizar' : 'crear'} el programa`,
          'error'
        );
      }
    },
  });

  // Función para formatear la tarifa como número con 2 decimales
  const formatTarifa = (tarifa) => {
    // Convertir a número si es string y luego usar toFixed
    return Number(tarifa).toFixed(2);
  };

  // Filtrar programas según los filtros seleccionados
  const filteredProgramas = programas.filter(programa => {
    let match = true;
    
    if (filters.especialidad_id && programa.especialidad_id !== parseInt(filters.especialidad_id)) {
      match = false;
    }
    
    if (filters.modalidad && programa.modalidad !== filters.modalidad) {
      match = false;
    }
    
    return match;
  });

  // Abrir diálogo de creación
  const handleCreateClick = () => {
    setEditMode(false);
    formik.resetForm();
    setDialogOpen(true);
  };

  // Abrir diálogo de edición
  const handleEditClick = (programa) => {
    setEditMode(true);
    setSelectedPrograma(programa);
    formik.setValues({
      nombre: programa.nombre,
      descripcion: programa.descripcion || '',
      especialidad_id: programa.especialidad_id,
      color: programa.color,
      duracion_cita: programa.duracion_cita,
      tarifa: programa.tarifa,
      modalidad: programa.modalidad,
      edad_minima: programa.edad_minima === null ? '' : programa.edad_minima,
      edad_maxima: programa.edad_maxima === null ? '' : programa.edad_maxima,
      activo: programa.activo
    });
    setDialogOpen(true);
  };

  // Cerrar diálogo
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Abrir diálogo de confirmación para eliminar
  const handleDeleteClick = (programa) => {
    setSelectedPrograma(programa);
    setDeleteDialogOpen(true);
  };

  // Confirmar eliminación de programa
  const handleDeleteConfirm = async () => {
    try {
      await programaService.deletePrograma(selectedPrograma.id);
      showNotification('Programa eliminado correctamente', 'success');
      setRefreshKey(old => old + 1); // Refrescar lista
    } catch (error) {
      console.error('Error deleting programa:', error);
      showNotification('Error al eliminar el programa', 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Manejar cambios en los filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Limpiar filtros
  const handleClearFilters = () => {
    setFilters({
      especialidad_id: '',
      modalidad: ''
    });
  };

  // Funciones para el selector de color
  const handleColorClick = (event) => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchorEl(null);
  };

  const handleColorSelect = (color) => {
    formik.setFieldValue('color', color);
    handleColorClose();
  };

  // Método para manejar cambio directo en el input de color
  const handleColorInputChange = (e) => {
    formik.setFieldValue('color', e.target.value);
  };

  // Renderizar el campo de filtros
  const renderFilters = () => (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
      <Typography variant="subtitle1" gutterBottom>
        Filtros
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Especialidad</InputLabel>
            <Select
              name="especialidad_id"
              value={filters.especialidad_id}
              onChange={handleFilterChange}
              label="Especialidad"
            >
              <MenuItem value="">Todas las especialidades</MenuItem>
              {especialidades.map(especialidad => (
                <MenuItem key={especialidad.id} value={especialidad.id}>
                  {especialidad.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Modalidad</InputLabel>
            <Select
              name="modalidad"
              value={filters.modalidad}
              onChange={handleFilterChange}
              label="Modalidad"
            >
              <MenuItem value="">Todas las modalidades</MenuItem>
              <MenuItem value="Regular">Regular</MenuItem>
              <MenuItem value="Apoyo social">Apoyo social</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={handleClearFilters}
            sx={{ ml: 1 }}
          >
            Limpiar filtros
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3 
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Gestión de Programas
        </Typography>
        <Box>
          <IconButton 
            onClick={() => setFiltersVisible(!filtersVisible)}
            color={filtersVisible ? 'primary' : 'default'}
            sx={{ mr: 1 }}
          >
            <FilterListIcon />
          </IconButton>
          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            >
              Nuevo Programa
            </Button>
          )}
        </Box>
      </Box>

      {filtersVisible && renderFilters()}

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : programas.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No se encontraron programas registrados.
              </Typography>
              {isAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                  sx={{ mt: 2 }}
                >
                  Crear Nuevo Programa
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Especialidad</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Modalidad</TableCell>
                    <TableCell>Rango Edad</TableCell>
                    <TableCell>Duración</TableCell>
                    <TableCell>Tarifa</TableCell>
                    <TableCell>Color</TableCell>
                    {isAdmin && <TableCell align="right">Acciones</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProgramas.map((programa) => {
                    // Buscar la especialidad asociada
                    const especialidad = especialidades.find(e => e.id === programa.especialidad_id);
                    
                    return (
                      <TableRow key={programa.id}>
                        <TableCell>{programa.nombre}</TableCell>
                        <TableCell>{especialidad?.nombre || '-'}</TableCell>
                        <TableCell>{programa.descripcion || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={programa.modalidad} 
                            color={programa.modalidad === 'Apoyo social' ? 'primary' : 'default'} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          {programa.edad_minima && programa.edad_maxima 
                            ? `${programa.edad_minima} a ${programa.edad_maxima} años`
                            : programa.edad_minima 
                              ? `Desde ${programa.edad_minima} años`
                              : programa.edad_maxima 
                                ? `Hasta ${programa.edad_maxima} años`
                                : 'Sin restricción'}
                        </TableCell>
                        <TableCell>{programa.duracion_cita} minutos</TableCell>
                        <TableCell>S/ {formatTarifa(programa.tarifa)}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: programa.color,
                                mr: 1,
                                border: '1px solid #ddd'
                              }}
                            />
                            {programa.color}
                          </Box>
                        </TableCell>
                        {isAdmin && (
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditClick(programa)}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(programa)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo para crear/editar programa */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {editMode ? 'Editar Programa' : 'Nuevo Programa'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="nombre"
                  name="nombre"
                  label="Nombre *"
                  value={formik.values.nombre}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.nombre && Boolean(formik.errors.nombre)}
                  helperText={formik.touched.nombre && formik.errors.nombre}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl 
                  fullWidth 
                  error={formik.touched.especialidad_id && Boolean(formik.errors.especialidad_id)}
                >
                  <InputLabel id="especialidad-label">Especialidad *</InputLabel>
                  <Select
                    labelId="especialidad-label"
                    id="especialidad_id"
                    name="especialidad_id"
                    value={formik.values.especialidad_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Especialidad *"
                  >
                    {especialidades.map(especialidad => (
                      <MenuItem key={especialidad.id} value={especialidad.id}>
                        {especialidad.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.especialidad_id && formik.errors.especialidad_id && (
                    <FormHelperText>{formik.errors.especialidad_id}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="descripcion"
                  name="descripcion"
                  label="Descripción"
                  value={formik.values.descripcion}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl 
                  fullWidth 
                  error={formik.touched.modalidad && Boolean(formik.errors.modalidad)}
                >
                  <InputLabel id="modalidad-label">Modalidad *</InputLabel>
                  <Select
                    labelId="modalidad-label"
                    id="modalidad"
                    name="modalidad"
                    value={formik.values.modalidad}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Modalidad *"
                  >
                    <MenuItem value="Regular">Regular</MenuItem>
                    <MenuItem value="Apoyo social">Apoyo social</MenuItem>
                  </Select>
                  {formik.touched.modalidad && formik.errors.modalidad && (
                    <FormHelperText>{formik.errors.modalidad}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="color"
                  name="color"
                  label="Color *"
                  value={formik.values.color}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.color && Boolean(formik.errors.color)}
                  helperText={formik.touched.color && formik.errors.color}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: formik.values.color,
                          mr: 1,
                          border: '1px solid #ddd'
                        }}
                      />
                    ),
                    endAdornment: (
                      <Tooltip title="Seleccionar color">
                        <IconButton 
                          size="small"
                          onClick={handleColorClick}
                        >
                          <ColorLensIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
                <Popover
                  open={colorPickerOpen}
                  anchorEl={colorAnchorEl}
                  onClose={handleColorClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Selecciona un color
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', width: 220, gap: 1 }}>
                      {colorPresets.map((color) => (
                        <Box
                          key={color}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '4px',
                            backgroundColor: color,
                            cursor: 'pointer',
                            border: color === formik.values.color ? '2px solid #000' : '1px solid #ddd',
                            '&:hover': {
                              opacity: 0.8,
                              transform: 'scale(1.05)',
                            },
                          }}
                          onClick={() => handleColorSelect(color)}
                        />
                      ))}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                        Selector de color personalizado:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={formik.values.color}
                          onChange={handleColorInputChange}
                          style={{ 
                            width: '100%', 
                            height: 40,
                            padding: 0,
                            border: 'none'
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Popover>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="edad_minima"
                  name="edad_minima"
                  label="Edad Mínima"
                  type="number"
                  value={formik.values.edad_minima}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.edad_minima && Boolean(formik.errors.edad_minima)}
                  helperText={formik.touched.edad_minima && formik.errors.edad_minima}
                  InputProps={{
                    inputProps: { min: 0, max: 120 }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="edad_maxima"
                  name="edad_maxima"
                  label="Edad Máxima"
                  type="number"
                  value={formik.values.edad_maxima}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.edad_maxima && Boolean(formik.errors.edad_maxima)}
                  helperText={formik.touched.edad_maxima && formik.errors.edad_maxima}
                  InputProps={{
                    inputProps: { min: 0, max: 120 }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="duracion_cita"
                  name="duracion_cita"
                  label="Duración (min) *"
                  type="number"
                  value={formik.values.duracion_cita}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.duracion_cita && Boolean(formik.errors.duracion_cita)}
                  helperText={formik.touched.duracion_cita && formik.errors.duracion_cita}
                  InputProps={{
                    inputProps: { min: 15, max: 180 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="tarifa"
                  name="tarifa"
                  label="Tarifa (S/) *"
                  type="number"
                  value={formik.values.tarifa}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.tarifa && Boolean(formik.errors.tarifa)}
                  helperText={formik.touched.tarifa && formik.errors.tarifa}
                  InputProps={{
                    inputProps: { min: 0, step: 0.1 }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="secondary">
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <CircularProgress size={24} />
              ) : editMode ? (
                'Actualizar'
              ) : (
                'Crear'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar Programa</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el programa "{selectedPrograma?.nombre}"? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgramasList;